// The sign-in door (contract section 26): invitation-only explanation,
// "Continue with Discord" (POST auth/discord/start then redirect to
// authorizationUrl), password sign-in retained for the owner and for
// finishing a Discord link, and the access-check state. Timeouts render
// Provider unavailable; nothing spins forever.
import { useCallback, useState, type FormEvent, type ReactNode } from 'react';
import { DiscordLogo } from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import type { AccessState } from '../../api/hooks';
import { postDiscordStart, postLogin } from '../../api/endpoints';
import { useElapsedLine } from '../../states/useElapsed';
import { HonestState } from '../../states/HonestState';
import { stateOfError } from '../../states/stateOf';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Input';
import { StatePill } from '../../ui/StatePill';
import { AuthFrame } from './AuthFrame';
import { challengeKindOf, loginOutcomeOf } from './loginOutcome';
import { isLocalReviewHost } from './localReview';
import { useAuthRefresh } from './useAuthRefresh';

export interface SignInDoorProps {
  readonly access: AccessState;
  readonly accessError: unknown;
  readonly onRetryAccess: () => void;
  // Rendered above the actions: a callback result, an expired-session notice.
  readonly notice?: ReactNode;
  readonly passwordHint?: string | undefined;
  // Tests can pin either origin. In the product, loopback is detected from
  // location so the local live-data review never starts production OAuth.
  readonly localReview?: boolean | undefined;
  // 409 and 428 are the two access challenges, not failures: the door hands
  // them straight to the challenge screen instead of printing an apology.
  readonly onChallenge: (kind: 'workspace' | 'password') => void;
}

// The bottom strip already carries "Invitation only" as a standing fact and
// the foot explains what an invitation is, so the lede says the one thing
// neither of them does: which two identities open this door.
const INVITE_ONLY = 'Sign in with the Discord identity the workspace owner invited, or with the email and password on your invitation.';

export function SignInDoor({
  access,
  accessError,
  onRetryAccess,
  notice,
  passwordHint,
  onChallenge,
  localReview = isLocalReviewHost(window.location.hostname),
}: SignInDoorProps) {
  const refresh = useAuthRefresh();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(Boolean(passwordHint) || localReview);

  // Opening the form unmounts the control that was clicked, and focus fell to
  // <body>: the next Tab restarted at the top of the document instead of
  // landing in the field the reader just asked for. The callback ref moves
  // focus the moment the input exists, which is also what a person filling in
  // a form expects the click to have done.
  const focusFirstField = useCallback((node: HTMLInputElement | null) => {
    if (node && document.activeElement === document.body) node.focus();
  }, []);

  const discord = useMutation({
    mutationFn: postDiscordStart,
    onSuccess: (data) => { window.location.assign(data.authorizationUrl); },
  });
  const login = useMutation({
    mutationFn: postLogin,
    onSuccess: () => refresh(),
    onError: (error) => {
      const kind = challengeKindOf(loginOutcomeOf(error).next);
      if (kind) onChallenge(kind);
    },
  });

  // Contract "States": Loading names the request and its elapsed time after
  // 2 s, so a slow answer is never a silent block.
  const loginElapsed = useElapsedLine(login.isPending);

  const onLogin = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!identifier.trim() || !password) return;
    login.mutate({ identifier: identifier.trim(), password });
  };

  // The state the observed failure earns, never a fixed one: a 429 reads
  // "Rate limited", a timeout reads "Provider unavailable". Hardcoding the
  // latter labelled a rate limit as an outage on the door.
  const unavailable = access === 'unavailable';
  const accessState = stateOfError(accessError);

  const accessBlock = unavailable
    ? <HonestState variant={accessState.variant} title="Access check unavailable" reason={accessState.reason} nextAction={{ label: 'Retry access check', onSelect: onRetryAccess }} />
    : null;

  // The same fact twice, once as the pill in the top strip and once as the
  // line in the bottom strip, exactly as the shell prints a state.
  const doorState = (
    <StatePill
      variant={unavailable ? accessState.variant : 'Authentication required'}
      reason={unavailable
        ? accessState.reason
        : 'This browser holds no workspace session, so nothing in the workspace is loaded.'}
      nextAction={unavailable
        ? { label: 'Retry access check', onSelect: onRetryAccess }
        : localReview
          ? { label: 'Use email and password', onSelect: () => setShowPassword(true) }
          : { label: 'Continue with Discord', onSelect: () => discord.mutate() }}
    />
  );
  const statusNote = unavailable ? 'Access check did not answer' : 'Access check: no session in this browser';

  const discordBlock = discord.isError
    ? <HonestState variant={stateOfError(discord.error).variant === 'Rate limited' ? 'Rate limited' : 'Provider unavailable'} title="Discord sign-in unavailable" reason={stateOfError(discord.error).reason} nextAction={[{ label: 'Try again', onSelect: () => discord.mutate() }, { label: 'Use email and password', onSelect: () => setShowPassword(true) }]} />
    : null;

  const outcome = login.isError ? loginOutcomeOf(login.error) : null;
  // A challenge has already routed away from this screen; nothing to print.
  const loginOutcome = outcome && challengeKindOf(outcome.next) === null ? outcome : null;

  return (
    <AuthFrame
      title="Sign in to your workspace"
      where="Sign in"
      lede={INVITE_ONLY}
      state={doorState}
      statusNote={statusNote}
      foot="No public sign-up. An invitation names a Discord user id or an email and expires within 7 days. Sessions are same-origin cookies; nothing is stored in this browser beyond your layout choices."
    >
      {notice}
      {accessBlock}
      <div className="door-row">
        <Button variant="primary" icon={<DiscordLogo weight="fill" />} disabled={discord.isPending || discord.isSuccess} onClick={() => discord.mutate()}>
          {discord.isPending ? 'Opening Discord' : discord.isSuccess ? 'Redirecting to Discord' : 'Continue with Discord'}
        </Button>
        {!showPassword ? <Button variant="tertiary" onClick={() => setShowPassword(true)}>Use email and password</Button> : null}
      </div>
      {discordBlock}
      {showPassword ? (
        <form className="door-form" onSubmit={onLogin} aria-label="Password sign-in">
          <h2>Email and password</h2>
          {passwordHint ? <p className="door-note">{passwordHint}</p> : null}
          <label className="door-lbl" htmlFor="door-identifier">Email or Discord user id</label>
          <Field ref={focusFirstField} id="door-identifier" name="identifier" autoComplete="username" value={identifier} onChange={(event) => setIdentifier(event.target.value)} required />
          <label className="door-lbl" htmlFor="door-password">Password</label>
          <Field id="door-password" name="password" type="password" revealable autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required />
          <div className="door-row">
            <Button variant="secondary" type="submit" disabled={login.isPending}>{login.isPending ? 'Signing in' : 'Sign in'}</Button>
            <a href="?reset_password=request">Forgot password</a>
          </div>
          {login.isPending ? <HonestState variant="Loading" reason="Waiting for the sign-in answer (15 s limit)." detail={loginElapsed} nextAction={{ label: 'Cancel', onSelect: () => login.reset() }} /> : null}
          {loginOutcome ? (
            <HonestState
              variant={loginOutcome.variant}
              title="Sign-in did not complete"
              reason={loginOutcome.reason}
              nextAction={loginOutcome.next === 'wait'
                ? { label: 'Try again later', onSelect: () => login.reset() }
                : loginOutcome.next === 'verify'
                  ? { label: 'Open the verification email', onSelect: () => login.reset() }
                  : { label: 'Try again', onSelect: () => login.reset() }}
            />
          ) : null}
        </form>
      ) : null}
    </AuthFrame>
  );
}
